import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import {
  LEAD_SOURCES, URGENCY_LEVELS, SERVICE_INTERESTS, LEAD_STATUSES,
  CONTACT_METHODS, RELATIONSHIPS, labelOf, optOf,
} from "@/config/leadOptions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PopoutDialog } from "@/components/ui/PopoutDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Plus, Search, Phone, Mail, CalendarPlus, UserCheck, Pencil, UserPlus,
  Lightbulb, X,
} from "lucide-react";
import { toast } from "sonner";

const URG_TONE = {
  red: { bg: "rgba(156,30,34,0.10)", color: "#9C1E22", border: "rgba(156,30,34,0.24)" },
  amber: { bg: "rgba(183,121,31,0.14)", color: "#9A6516", border: "rgba(183,121,31,0.30)" },
  sky: { bg: "rgba(155,185,206,0.25)", color: "#1C4358", border: "rgba(155,185,206,0.45)" },
  gray: { bg: "rgba(75,85,99,0.10)", color: "#4B5563", border: "rgba(75,85,99,0.22)" },
};
const UrgencyBadge = ({ value }) => {
  const o = optOf(URGENCY_LEVELS, value);
  if (!o) return <span className="text-muted-foreground">—</span>;
  const t = URG_TONE[o.tone] || URG_TONE.gray;
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border whitespace-nowrap"
      style={{ backgroundColor: t.bg, color: t.color, borderColor: t.border }}>{o.label}</span>
  );
};

const emptyForm = {
  first_name: "", last_name: "", phone: "", email: "", city: "", state_province: "", country: "USA",
  relationship_to_loved_one: "", loved_one_name: "", loved_one_city: "", loved_one_state_province: "",
  urgency_level: "", service_interest: "", main_concern: "", preferred_contact_method: "",
  best_time_to_contact: "", notes: "", email_consent: false, sms_consent_future: false,
  lead_source: "manual_entry", assigned_to_user_id: "", branch_id: "", status: "new",
};

export default function Leads() {
  const { user } = useAuth();
  const canWrite = Array.isArray(user?.writable_resources) && user.writable_resources.includes("leads");
  const canConvert = !!user?.can_convert;

  const [leads, setLeads] = useState([]);
  const [staff, setStaff] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", status: "all", urgency_level: "all", service_interest: "all", assigned_to_user_id: "all", branch_id: "all" });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [suggested, setSuggested] = useState(null); // { lead }

  const [bookOpen, setBookOpen] = useState(false);
  const [bookLead, setBookLead] = useState(null);
  const [bookForm, setBookForm] = useState({ date: "", time: "", mode: "video", notes: "" });

  const staffName = (id) => staff.find((s) => s.id === id)?.full_name || "";
  const leadName = (l) => [l.first_name, l.last_name].filter(Boolean).join(" ") || l.name || l.contact_name || "Unnamed lead";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      ["status", "urgency_level", "service_interest", "assigned_to_user_id", "branch_id"].forEach((k) => {
        if (filters[k] && filters[k] !== "all") params[k] = filters[k];
      });
      const { data } = await api.get("/leads", { params });
      setLeads(data.items || []);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    api.get("/staff").then((r) => setStaff(r.data.items || [])).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Quick-add from dashboard (?new=1)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1" && canWrite) {
      openNew();
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, canWrite]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const openNew = () => {
    const defaultBranch = branches.length === 1 ? branches[0].id : "";
    setEditing(null);
    setForm({ ...emptyForm, branch_id: defaultBranch, assigned_to_user_id: user?.id || "" });
    setOpen(true);
  };
  const openEdit = (l) => {
    setEditing(l);
    setForm({ ...emptyForm, ...l, branch_id: l.branch_id || "", assigned_to_user_id: l.assigned_to_user_id || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name && !form.last_name) { toast.error("Add at least a first or last name"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.branch_id === "") delete payload.branch_id;
      if (payload.assigned_to_user_id === "") delete payload.assigned_to_user_id;
      if (editing) {
        await api.put(`/leads/${editing.id}`, payload);
        toast.success("Lead updated");
        setOpen(false);
        await load();
      } else {
        const { data } = await api.post("/leads/intake", payload);
        setOpen(false);
        await load();
        setSuggested({ lead: data.lead });
        toast.success("Lead created · follow-up task added & coordinator notified");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save lead");
    } finally { setSubmitting(false); }
  };

  const openBook = (l) => {
    setBookLead(l);
    setBookForm({ date: "", time: "10:00", mode: "video", notes: "" });
    setBookOpen(true);
  };
  const submitBook = async (e) => {
    e.preventDefault();
    if (!bookForm.date) { toast.error("Pick a date"); return; }
    try {
      const scheduled_at = `${bookForm.date}T${bookForm.time || "10:00"}:00`;
      await api.post("/consultations", {
        lead_id: bookLead.id, family_name: leadName(bookLead), scheduled_at,
        mode: bookForm.mode, status: "scheduled", notes_before: bookForm.notes,
        branch_id: bookLead.branch_id,
      });
      await api.put(`/leads/${bookLead.id}`, { status: "consultation_booked" });
      toast.success("Free consultation booked");
      setBookOpen(false);
      setSuggested(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not book consultation");
    }
  };

  const convert = async (l) => {
    try {
      const { data } = await api.post(`/leads/${l.id}/convert`, {});
      toast.success(`Converted to case ${data.case?.case_number || ""}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not convert lead");
    }
  };

  const FilterSelect = ({ value, onChange, options, placeholder, testid, all = "All" }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-full sm:w-[160px]" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{all}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2">
            <UserPlus size={22} className="text-[#1C4358]" /> Lead Intake
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">Capture incoming families and move them toward a free consultation.</p>
        </div>
        {canWrite && (
          <Button onClick={openNew} data-testid="new-lead-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>
            <Plus size={17} className="mr-1.5" /> New Lead
          </Button>
        )}
      </div>

      {/* Suggested next step banner */}
      {suggested && (
        <Card className="p-4 mb-4 border-0" style={{ background: "linear-gradient(100deg, rgba(28,67,88,0.06), rgba(155,185,206,0.18))" }} data-testid="suggested-next-step">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(222,224,41,0.30)" }}>
              <Lightbulb size={18} style={{ color: "#6E700F" }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-[#252221]">Suggested next step for {leadName(suggested.lead)}</div>
              <p className="text-sm text-muted-foreground">Book the free one-hour family transition consultation.</p>
            </div>
            <Button size="sm" onClick={() => openBook(suggested.lead)} data-testid="suggested-book-button" className="text-white shrink-0" style={{ backgroundColor: "var(--mmm-navy)" }}>
              <CalendarPlus size={15} className="mr-1.5" /> Book Consultation
            </Button>
            <button onClick={() => setSuggested(null)} className="p-1 text-muted-foreground hover:text-[#252221]" data-testid="dismiss-suggested"><X size={16} /></button>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-3 mb-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={filters.q} onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
              placeholder="Search name, phone, email, loved one..." className="pl-9 h-9" data-testid="lead-search" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex gap-2">
            <FilterSelect value={filters.urgency_level} onChange={(v) => setFilters((p) => ({ ...p, urgency_level: v }))} options={URGENCY_LEVELS} placeholder="Urgency" testid="filter-urgency" all="All urgency" />
            <FilterSelect value={filters.service_interest} onChange={(v) => setFilters((p) => ({ ...p, service_interest: v }))} options={SERVICE_INTERESTS.map((s) => ({ value: s.value, label: s.short }))} placeholder="Interest" testid="filter-interest" all="All interests" />
            <FilterSelect value={filters.status} onChange={(v) => setFilters((p) => ({ ...p, status: v }))} options={LEAD_STATUSES} placeholder="Status" testid="filter-status" all="All status" />
            <FilterSelect value={filters.assigned_to_user_id} onChange={(v) => setFilters((p) => ({ ...p, assigned_to_user_id: v }))} options={staff.map((s) => ({ value: s.id, label: s.full_name }))} placeholder="Assigned" testid="filter-assigned" all="Anyone" />
            <FilterSelect value={filters.branch_id} onChange={(v) => setFilters((p) => ({ ...p, branch_id: v }))} options={branches.map((b) => ({ value: b.id, label: b.name }))} placeholder="Branch" testid="filter-branch" all="All branches" />
          </div>
        </div>
      </Card>

      {/* List */}
      <div className="rounded-xl border border-[#E6EDF2] bg-white overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        {loading ? (
          <div className="p-4 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : leads.length === 0 ? (
          <div className="py-16 text-center">
            <div className="mx-auto mb-3 h-14 w-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}>
              <UserPlus size={26} className="text-[#1C4358]" />
            </div>
            <p className="font-medium text-[#252221]">No leads found</p>
            <p className="text-sm text-muted-foreground">Adjust your filters or add a new lead to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto mmm-scroll">
            <Table data-testid="leads-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Lead</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Interest</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Urgency</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Assigned</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80 text-right">Quick actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((l) => (
                  <TableRow key={l.id} className="hover:bg-[rgba(155,185,206,0.10)]" data-testid="lead-row">
                    <TableCell className="py-3">
                      <div className="font-semibold text-[#252221]">{leadName(l)}</div>
                      <div className="text-xs text-muted-foreground">
                        {l.loved_one_name ? `For ${l.loved_one_name}` : ""}{l.city ? `${l.loved_one_name ? " · " : ""}${l.city}${l.state_province ? ", " + l.state_province : ""}` : ""}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-[#252221]">{labelOf(SERVICE_INTERESTS.map((s) => ({ value: s.value, label: s.short })), l.service_interest) || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell><UrgencyBadge value={l.urgency_level} /></TableCell>
                    <TableCell><StatusBadge value={l.status} label={labelOf(LEAD_STATUSES, l.status)} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{staffName(l.assigned_to_user_id) || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 justify-end">
                        <a href={l.phone ? `tel:${l.phone}` : undefined} onClick={(e) => !l.phone && e.preventDefault()}
                          className={`p-1.5 rounded-lg ${l.phone ? "text-[#1C4358] hover:bg-[rgba(28,67,88,0.08)]" : "text-gray-300 cursor-not-allowed"}`} title="Call" data-testid="quick-call"><Phone size={15} /></a>
                        <a href={l.email ? `mailto:${l.email}` : undefined} onClick={(e) => !l.email && e.preventDefault()}
                          className={`p-1.5 rounded-lg ${l.email ? "text-[#1C4358] hover:bg-[rgba(28,67,88,0.08)]" : "text-gray-300 cursor-not-allowed"}`} title="Email" data-testid="quick-email"><Mail size={15} /></a>
                        {canWrite && (
                          <button onClick={() => openBook(l)} className="p-1.5 rounded-lg text-[#2F7D5A] hover:bg-[rgba(47,125,90,0.10)]" title="Book consultation" data-testid="book-consult-button"><CalendarPlus size={15} /></button>
                        )}
                        {canConvert && l.status !== "converted" && (
                          <button onClick={() => convert(l)} className="p-1.5 rounded-lg text-[#9C1E22] hover:bg-[rgba(156,30,34,0.08)]" title="Convert to case" data-testid="convert-lead-button"><UserCheck size={15} /></button>
                        )}
                        {canWrite && (
                          <button onClick={() => openEdit(l)} className="p-1.5 rounded-lg text-[#1C4358] hover:bg-[rgba(28,67,88,0.08)]" title="Edit" data-testid="edit-lead-button"><Pencil size={15} /></button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-2">{leads.length} lead{leads.length === 1 ? "" : "s"}</p>

      {/* Intake form dialog — modal by default, detachable to a floating pop-out */}
      <PopoutDialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? "Edit Lead" : "New Lead Intake"}
        subtitle="Lead Intake · Leads"
        icon={UserPlus}
        maxWidthClass="max-w-2xl"
        testid="lead-form-dialog"
      >
        <form onSubmit={submit} className="space-y-5">
          <p className="text-sm text-muted-foreground -mt-1">{editing ? "Update this lead's details." : "Capture the family's details. A follow-up task is created automatically."}</p>
            {/* Contact */}
            <fieldset className="space-y-3">
              <legend className="text-xs font-bold uppercase tracking-wide text-[#1C4358] mb-1">Family Contact</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block text-[13px]">First name *</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="lead-first-name" /></div>
                <div><Label className="mb-1.5 block text-[13px]">Last name</Label><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="lead-last-name" /></div>
                <div><Label className="mb-1.5 block text-[13px]">Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="lead-phone" /></div>
                <div><Label className="mb-1.5 block text-[13px]">Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="lead-email" /></div>
                <div><Label className="mb-1.5 block text-[13px]">City</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="lead-city" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="mb-1.5 block text-[13px]">State / Prov.</Label><Input value={form.state_province} onChange={(e) => set("state_province", e.target.value)} data-testid="lead-state" /></div>
                  <div><Label className="mb-1.5 block text-[13px]">Country</Label>
                    <Select value={form.country} onValueChange={(v) => set("country", v)}>
                      <SelectTrigger data-testid="lead-country"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="USA">USA</SelectItem><SelectItem value="Canada">Canada</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </fieldset>

            {/* Loved one */}
            <fieldset className="space-y-3">
              <legend className="text-xs font-bold uppercase tracking-wide text-[#1C4358] mb-1">Loved One (Mama)</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block text-[13px]">Relationship to loved one</Label>
                  <Select value={form.relationship_to_loved_one} onValueChange={(v) => set("relationship_to_loved_one", v)}>
                    <SelectTrigger data-testid="lead-relationship"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{RELATIONSHIPS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="mb-1.5 block text-[13px]">Loved one's name</Label><Input value={form.loved_one_name} onChange={(e) => set("loved_one_name", e.target.value)} data-testid="lead-loved-one-name" /></div>
                <div><Label className="mb-1.5 block text-[13px]">Loved one's city</Label><Input value={form.loved_one_city} onChange={(e) => set("loved_one_city", e.target.value)} data-testid="lead-loved-one-city" /></div>
                <div><Label className="mb-1.5 block text-[13px]">Loved one's state / prov.</Label><Input value={form.loved_one_state_province} onChange={(e) => set("loved_one_state_province", e.target.value)} data-testid="lead-loved-one-state" /></div>
              </div>
            </fieldset>

            {/* Intake details */}
            <fieldset className="space-y-3">
              <legend className="text-xs font-bold uppercase tracking-wide text-[#1C4358] mb-1">Intake Details</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><Label className="mb-1.5 block text-[13px]">Service interest</Label>
                  <Select value={form.service_interest} onValueChange={(v) => set("service_interest", v)}>
                    <SelectTrigger data-testid="lead-service-interest"><SelectValue placeholder="What do they need help with?" /></SelectTrigger>
                    <SelectContent>{SERVICE_INTERESTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="mb-1.5 block text-[13px]">Urgency level</Label>
                  <Select value={form.urgency_level} onValueChange={(v) => set("urgency_level", v)}>
                    <SelectTrigger data-testid="lead-urgency"><SelectValue placeholder="How soon?" /></SelectTrigger>
                    <SelectContent>{URGENCY_LEVELS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="mb-1.5 block text-[13px]">Preferred contact method</Label>
                  <Select value={form.preferred_contact_method} onValueChange={(v) => set("preferred_contact_method", v)}>
                    <SelectTrigger data-testid="lead-contact-method"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{CONTACT_METHODS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="mb-1.5 block text-[13px]">Best time to contact</Label><Input value={form.best_time_to_contact} onChange={(e) => set("best_time_to_contact", e.target.value)} placeholder="e.g. Weekday mornings" data-testid="lead-best-time" /></div>
                <div><Label className="mb-1.5 block text-[13px]">Lead source</Label>
                  <Select value={form.lead_source} onValueChange={(v) => set("lead_source", v)}>
                    <SelectTrigger data-testid="lead-source"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{LEAD_SOURCES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2"><Label className="mb-1.5 block text-[13px]">Main concern</Label><Textarea value={form.main_concern} onChange={(e) => set("main_concern", e.target.value)} rows={2} placeholder="What is the family most worried about?" data-testid="lead-main-concern" /></div>
                <div className="sm:col-span-2"><Label className="mb-1.5 block text-[13px]">Notes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="lead-notes" /></div>
              </div>
            </fieldset>

            {/* Assignment */}
            <fieldset className="space-y-3">
              <legend className="text-xs font-bold uppercase tracking-wide text-[#1C4358] mb-1">Assignment</legend>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label className="mb-1.5 block text-[13px]">Intake coordinator</Label>
                  <Select value={form.assigned_to_user_id} onValueChange={(v) => set("assigned_to_user_id", v)}>
                    <SelectTrigger data-testid="lead-assigned"><SelectValue placeholder="Assign" /></SelectTrigger>
                    <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} · {s.role_label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="mb-1.5 block text-[13px]">Branch</Label>
                  <Select value={form.branch_id} onValueChange={(v) => set("branch_id", v)}>
                    <SelectTrigger data-testid="lead-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="mb-1.5 block text-[13px]">Lead status</Label>
                  <Select value={form.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger data-testid="lead-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{LEAD_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex flex-col gap-2.5 pt-1">
                <label className="flex items-center gap-2.5 cursor-pointer" data-testid="lead-email-consent">
                  <Checkbox checked={form.email_consent} onCheckedChange={(v) => set("email_consent", !!v)} />
                  <span className="text-sm text-[#252221]">Email consent — family agrees to receive emails</span>
                </label>
                <label className="flex items-center gap-2.5 cursor-not-allowed opacity-60" title="Inactive until SMS compliance is enabled">
                  <Checkbox checked={form.sms_consent_future} disabled onCheckedChange={(v) => set("sms_consent_future", !!v)} data-testid="lead-sms-consent" />
                  <span className="text-sm text-[#252221]">Future SMS consent <span className="text-xs text-muted-foreground">(optional — inactive until compliance is enabled)</span></span>
                </label>
              </div>
            </fieldset>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="lead-cancel-button">Cancel</Button>
              <Button type="submit" disabled={submitting} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="lead-submit-button">{submitting ? "Saving..." : (editing ? "Save Lead" : "Create Lead")}</Button>
            </div>
        </form>
      </PopoutDialog>

      {/* Book consultation dialog — modal by default, detachable to a floating pop-out */}
      <PopoutDialog
        open={bookOpen}
        onOpenChange={setBookOpen}
        title="Book Free Consultation"
        subtitle={bookLead ? `For ${leadName(bookLead)} · Consultations` : "Consultations"}
        icon={CalendarPlus}
        maxWidthClass="max-w-md"
        testid="book-consult-dialog"
      >
        <form onSubmit={submitBook} className="space-y-4">
          <p className="text-sm text-muted-foreground -mt-1">Schedule the free one-hour family transition consultation{bookLead ? ` for ${leadName(bookLead)}` : ""}.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-[13px]">Date *</Label><Input type="date" value={bookForm.date} onChange={(e) => setBookForm((p) => ({ ...p, date: e.target.value }))} data-testid="consult-date" /></div>
              <div><Label className="mb-1.5 block text-[13px]">Time</Label><Input type="time" value={bookForm.time} onChange={(e) => setBookForm((p) => ({ ...p, time: e.target.value }))} data-testid="consult-time" /></div>
            </div>
            <div><Label className="mb-1.5 block text-[13px]">Format</Label>
              <Select value={bookForm.mode} onValueChange={(v) => setBookForm((p) => ({ ...p, mode: v }))}>
                <SelectTrigger data-testid="consult-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="video">Video call</SelectItem>
                  <SelectItem value="phone">Phone call</SelectItem>
                  <SelectItem value="in_person">In person</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block text-[13px]">Notes (optional)</Label><Textarea value={bookForm.notes} onChange={(e) => setBookForm((p) => ({ ...p, notes: e.target.value }))} rows={2} data-testid="consult-notes" /></div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setBookOpen(false)}>Cancel</Button>
              <Button type="submit" className="text-white" style={{ backgroundColor: "var(--mmm-navy)" }} data-testid="consult-submit-button">Book Consultation</Button>
            </div>
        </form>
      </PopoutDialog>
    </div>
  );
}
